import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { XSquare } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { toast } from "sonner";

import { Slider } from "../ui/slider";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { convertImageFormat } from "@/lib/file-image.utils";
import { cn } from "@/lib/utils";
import Icons from "@/utils/Icons";
import { getCroppedImg } from "@/utils/image-helper";

type TImage = {
  file: File;
  preview: string;
};
type Crop = {
  x: number;
  y: number;
};
type dimension = {
  width: number;
  height: number;
};
type Tcrop = {
  image: TImage;
  open: boolean;
  onhandleCrop: () => void;
  addNewPicture: React.Dispatch<
    React.SetStateAction<TImage | null | undefined>
  >;
  setOpenCrop: React.Dispatch<React.SetStateAction<boolean>>;
  aspect: number;
  dimension?: dimension;
  strictCrop?: boolean;
};
export type Tcropped = {
  x: number;
  y: number;
  width: number;
  height: number;
};
export const DialogDemo: React.FC<Tcrop> = ({
  image,
  open,
  onhandleCrop,
  addNewPicture,
  setOpenCrop,
  aspect,
  dimension,
  strictCrop,
  //   dimension,
}) => {
  const [crop, setCrop] = useState<Crop>({ x: 0, y: 0 });

  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Tcropped | null>(
    null,
  );
  const [croppedImage, setCroppedImage] = useState<string | null>(null);
  const [removeCrop, setRemoveCrop] = useState(false);

  const urlToFile = async (
    url: string,
    filename: string,
    mimeType: string,
  ): Promise<File> => {
    const response = await fetch(url);
    const data = await response.blob();

    return new File([data], filename, { type: mimeType });
  };
  const handleRemoveCrop = (): void => {
    setRemoveCrop(true);
  };

  const showCroppedImage = async (): Promise<void> => {
    if (!croppedAreaPixels) {
      return;
    }
    try {
      const originalType = image?.file?.type ?? "image/jpg";
      const originalExt = originalType?.split("/").pop() ?? "jpg";

      const croppedImage = (await getCroppedImg(
        image.preview,
        croppedAreaPixels,
      )) as string;
      setCroppedImage(croppedImage);
      if (croppedImage) {
        const file = await urlToFile(
          croppedImage,
          `${image.file.name.split(".")[0]}.${originalExt}`,
          originalType,
        );

        addNewPicture({ file, preview: croppedImage } as TImage);
      }
      setRemoveCrop(false);
      onhandleCrop();
    } catch (e) {
      toast.error("Error cropping image!!!");
    }
  };

  const onCropComplete = (
    _croppedArea: Tcropped,
    croppedAreaPixels: Tcropped,
  ): void => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const oncropFinish = async (): Promise<void> => {
    handleRemoveCrop();
    await showCroppedImage();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={() => {
        if (strictCrop) {
          onhandleCrop();
          addNewPicture(null);
        }
        setOpenCrop(!open);
      }}
    >
      <DialogContent className='sm:max-w-[1200px] min-h-fit overflow-y-scroll scrollbar-thin'>
        <DialogHeader>
          <DialogTitle>Crop</DialogTitle>
          <VisuallyHidden>
            <DialogDescription />
          </VisuallyHidden>
        </DialogHeader>

        <div className='flex items-center justify-center min-h-[500px] w-full relative'>
          {removeCrop ? (
            <div>
              <img
                src={croppedImage ? croppedImage : ""}
                alt='cropped images'
                width={400}
                height={400}
              />
            </div>
          ) : (
            <Cropper
              image={image.preview}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
              cropSize={{
                height: dimension?.height || 266,
                width: dimension?.width || 226,
              }}
            />
          )}
        </div>
        <div className='flex items-center justify-center gap-10 m-10'>
          <div className='flex items-center space-x-4'>
            <h2>Zoom</h2>
            <div className='min-w-[220px]'>
              <Slider
                defaultValue={[1]}
                min={1}
                max={3}
                step={0.1}
                onValueChange={(value) => setZoom(value[0])}
              />
            </div>
          </div>
          <div className='flex items-center space-x-4'>
            <Button
              onClick={() => {
                onhandleCrop();
                addNewPicture(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={oncropFinish}>Crop</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

type TImageUploaderProps = {
  setData: (files: TImage | null | File) => void;
  imgName: string;
  aspect?: number;
  isImage?: boolean;
  dimension?: dimension;
};

const ImageUploader: React.FC<TImageUploaderProps> = ({
  aspect = 1,
  imgName,
  setData,
  isImage = false,
  dimension = {
    width: 596,
    height: 125,
  },
  ...props
}) => {
  const [openCrop, setOpenCrop] = useState(false);
  const [image, setImage] = useState<TImage | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [isimage, setisImage] = useState<boolean>(false);
  const [imageValue, setImageValue] = useState("");
  const [pictures, setPicture] = useState<TImage | null>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleClick = (): void => {
    fileInputRef.current?.click();
  };

  const handleCropClose = (): void => {
    setOpenCrop(!openCrop);
    setImage(null);
  };

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = async (
    e,
  ): Promise<void> => {
    const selectedFile = e.target.files?.[0];
    setImageValue(e.target.value);
    const allowedImageTypes = [
      "image/jpeg",
      "image/png",
      "image/jpg",
      "image/webp",
      "image/heic",
      "image/heif",
    ];

    if (selectedFile && allowedImageTypes.includes(selectedFile.type)) {
      if (selectedFile && selectedFile.size <= 2 * 1024 * 1024) {
        const imageFile = e.target.files?.[0] as File;

        const convertedFile = await convertImageFormat({
          file: imageFile ?? (e.target.files?.[0] as File),
          targetFormat: "jpeg",
          quality: 0.7,
        });

        const image = {
          file: convertedFile ?? imageFile,
          preview: URL.createObjectURL(convertedFile ?? imageFile),
        };
        setShowCropper(true);
        setOpenCrop(true);

        setImage(image as TImage);

        return;
      }
      setImageValue("");
      toast.error("Image size exceeds maximum!");

      return;
    }
    setImageValue("");
    toast.error("Image type is invalid!!!");
  };

  useEffect(() => {
    setisImage(isImage);
  }, [isImage]);

  useEffect(() => {
    if (pictures) {
      setData(pictures);
    }
  }, [pictures]);

  return (
    <>
      <div className='flex w-full'>
        {showCropper && (
          <div className='flex items-center gap-x-2'>
            <DialogDemo
              image={image ? image : ({} as TImage)}
              open={openCrop}
              setOpenCrop={setOpenCrop}
              onhandleCrop={handleCropClose}
              addNewPicture={setPicture}
              aspect={aspect}
              dimension={dimension}
            />
          </div>
        )}
        {!pictures && !isimage && (
          <div
            className='w-full px-4 py-2 bg-white border rounded-md'
            onClick={handleClick}
          >
            <Label
              htmlFor={imgName}
              className='flex items-center gap-4 cursor-pointer'
            >
              <Icons name='cloud-upload' />
              <p className='font-normal opacity-70 whitespace-nowrap'>
                Click here to upload
              </p>
            </Label>
          </div>
        )}
        <ScrollArea
          className={cn(!pictures && !isimage && "hidden", "text-wrap w-full")}
        >
          <div className='flex items-center w-full gap-x-2'>
            {(pictures || isimage) && (
              <div className='w-full px-4 py-1 border rounded-md'>
                <div className='flex items-center justify-between space-x-1 '>
                  <p className='text-sm font-thin'>
                    {pictures?.file?.name?.slice(0, 12) || "image"}
                  </p>
                  <XSquare
                    color='red'
                    onClick={() => {
                      setImageValue("");
                      setPicture(undefined);
                      setisImage(false);
                      setData(null);
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          <ScrollBar orientation='horizontal' />
        </ScrollArea>

        <Input
          id={imgName}
          ref={fileInputRef}
          type='file'
          accept='image/*'
          onChange={handleChange}
          className='hidden'
          value={imageValue}
          {...props}
        />
      </div>
    </>
  );
};

export default ImageUploader;
